import * as React from 'react'
import { getStyles } from './hooks/useStyles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  SimpleAccordion,
  SimpleAccordionDetails,
  SimpleAccordionSummary,
} from '@sales-center/components'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import { useGetBranchDetail } from '@user/store/branches/hooks/useGetBranchDetail'
import { emptyAddress, IAddress } from '@sales-center/common'
import { useGetBranchUrl } from '@user/store/authorization/hooks'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import { accordionClasses } from '@mui/material/Accordion'

interface Props {
  id: string
  name: string
}

const displayText = (inputText: string | undefined) =>
  !!inputText && inputText.trim().length > 0 ? inputText : '-'

export const BranchDetail: React.FC<Props> = (props) => {
  const { id, name } = props
  const { t } = useTranslation()
  const { status, branch, setBranchId } = useGetBranchDetail()
  const addr: IAddress =
    !!branch && !!branch.address ? branch.address : emptyAddress

  const onChange = React.useCallback(
    (_: React.ChangeEvent, isExpanded: boolean) => {
      if (isExpanded) setBranchId(id)
    },
    [id, setBranchId],
  )
  const branchUrl = useGetBranchUrl(id)

  const onClick = React.useCallback(() => {
    window.open(branchUrl, '_blank')
  }, [branchUrl])
  const styles = getStyles(useTheme())
  return (
    <SimpleAccordion
      onChange={onChange}
      square
      sx={{
        ...styles.accordion,
        ...styles.branch,
        [`&.${accordionClasses.expanded}`]: styles.branchExpanded,
      }}
    >
      <SimpleAccordionSummary expandIcon={<ExpandMoreIcon />}>
        {name}
      </SimpleAccordionSummary>
      <SimpleAccordionDetails>
        <Box sx={styles.branchDetail}>
          <Grid container item direction="row">
            <Grid
              container
              item
              direction="column"
              xs={3}
              sx={styles.branchLabel}
            >
              <Grid item>Branch Name</Grid>
              <Grid item>Company Name</Grid>
              <Grid item>Address</Grid>
              <Grid item>City</Grid>
              <Grid item>{t('state')}</Grid>
              <Grid item>{t('postalCode')}</Grid>
              <Grid item>Phone</Grid>
              <Grid item>Fax</Grid>
              <Grid item>Email</Grid>
              <Grid item>License Number</Grid>
              {!!branch && !!branch.manager && (
                <>
                  <Grid item>Branch Manager Email </Grid>
                  <Grid item>Branch Manager UserName </Grid>
                </>
              )}
              {!!branch && !!branch.routingAddress && (
                <>
                  <Grid item>Routing Address </Grid>
                  <Grid item>Routing City </Grid>
                  <Grid item>Routing {t('state')} </Grid>
                  <Grid item>Routing {t('postalCode')} </Grid>
                </>
              )}
            </Grid>
            {!!branch && branch.loaded ? (
              <Grid container item direction="column" xs={9}>
                <Grid item>{displayText(branch.name)}</Grid>
                <Grid item>{displayText(branch.companyName)}</Grid>
                <Grid item>
                  {displayText(addr.address + ' ' + addr.address2)}
                </Grid>
                <Grid item>{displayText(addr.city)}</Grid>
                <Grid item>{displayText(addr.state)}</Grid>
                <Grid item>{displayText(addr.postalCode)}</Grid>
                <Grid item>{displayText(branch.phone)}</Grid>
                <Grid item>{displayText(branch.fax)}</Grid>
                <Grid item>{displayText(branch.email)}</Grid>
                <Grid item>{displayText(branch.licenseNumber)}</Grid>
                {!!branch.manager && (
                  <>
                    <Grid item>{displayText(branch.manager.email)}</Grid>
                    <Grid item>{displayText(branch.manager.userName)}</Grid>
                  </>
                )}
                {!!branch.routingAddress && (
                  <>
                    <Grid item>
                      {displayText(
                        (branch.routingAddress.address ?? '') +
                          ' ' +
                          (branch.routingAddress.address2 ?? ''),
                      )}
                    </Grid>
                    <Grid item>{displayText(branch.routingAddress.city)}</Grid>
                    <Grid item>{displayText(branch.routingAddress.state)}</Grid>
                    <Grid item>
                      {displayText(branch.routingAddress.postalCode)}
                    </Grid>
                  </>
                )}
              </Grid>
            ) : (
              status == 'pending' && (
                <CircularProgress color="primary" sx={styles.loader} />
              )
            )}
          </Grid>

          {!!branch && branch.loaded && (
            <Button
              color="primary"
              variant="outlined"
              sx={styles.updateButton}
              onClick={onClick}
            >
              Update Branch Info
            </Button>
          )}
        </Box>
      </SimpleAccordionDetails>
    </SimpleAccordion>
  )
}
BranchDetail.displayName = 'BranchDetail'
